<template>
  <div>
    <h4 class="font-weight-bold m-0 mb-3" v-if="loading || totalAnalyses">
      <span class="text-muted font-weight-light">Insights</span> <span class="material-icons md-14 arrow-east">east</span> AI Form Analysis
    </h4>

    <div v-if="!loading && !totalAnalyses" class="card card-body gradient-border" style="line-height: 1.75rem;">
      <h3 class="text-primary">Introducing Eureka: AI-Powered Form Analysis</h3>
      <h4>Let our AI assistant analyse your forms and provide expert recommendations</h4>
      <p>Eureka automatically processes your form and field data to identify optimisation opportunities and provide actionable insights; just like our Customer Success team would do, but available whenever you need it.</p>
      <h4 class="font-weight-bolder">Eureka Analyses Your Forms For</h4>
      <ul>
        <li>🔍 <b>Conversion Leaks</b>: Identifies where users abandon your forms</li>
        <li>💻 <b>Device Discrepancies</b>: Compares performance across mobile, tablet, and desktop</li>
        <li>⌨️ <b>Field Performance</b>: Highlights problematic fields with high error rates or abandonment</li>
        <li>🤖 <b>Autofill Intelligence</b>: Shows how autofill affects completion rates</li>
        <li>📣 <b>Campaign Performance</b>: Analyses which traffic sources deliver better form submissions</li>
        <li>🏆 <b>Industry Benchmarks</b>: Places your performance in context with relevant comparisons</li>
      </ul>
      <h4 class="font-weight-bolder">What You'll Receive</h4>
      <ul>
        <li>⚡ <b>Instant Analysis:</b> Get comprehensive insights in minutes, a real time saver</li>
        <li>🎯 <b>Focused Recommendations:</b> Clear, prioritised actions to implement</li>
        <li>📤 <b>Shareable Results:</b> Ready-to-share slides</li>
      </ul>
      <div>
        <b-btn variant="primary" @click="toggleNewAnalysis">Start Your First AI Analysis <span class="material-icons md-14 arrow-east">east</span></b-btn>
      </div>
      <div class="blob">
        <span></span>
        <span></span>
      </div>
      <img class="blob-eureka" src="@/assets/eureka.png" width="200" height="200">
    </div>

    <div class="d-flex justify-content-between align-items-end" v-if="totalAnalyses">
      <a class="btn btn-create" @click="toggleNewAnalysis">
        <i class="material-symbols-outlined" style="vertical-align: text-bottom;">automation</i> RUN NEW AI FORM ANALYSIS
      </a>
      <div>
        <span class="small font-weight-bold text-uppercase">
          This Month’s AI Usage
        </span>
        <b-progress v-if="aiCreditsTotal" show-value
                    :variant="subscriptionUsageColor(aiCreditsUsedPct)"
                    v-b-tooltip :title="`${aiCreditsUsed} of ${aiCreditsTotal} AI credits is used`">
          <b-progress-bar :value="aiCreditsUsedPct">
            <span><strong>{{ Number.parseFloat(aiCreditsUsedPct).toFixed(0) }}%</strong></span>
          </b-progress-bar>
        </b-progress>
      </div>
    </div>

    <collapse-transition>
      <div class="card mt-3" v-if="showNewAnalysis">
        <h4 class="text-primary text-uppercase line-after">New AI Form Analysis</h4>
        <form class="d-flex flex-column" @submit.prevent="submitNewAnalysis">
          <div class="row d-none">
            <div class="col-2">
              <label class="mr-1 font-weight-bold">Select your goal</label>
            </div>
            <div class="col">
              <multiselect
                v-model="newAnalysis.selectedScenario" :options="availableScenarios" :multiple="false" :hide-selected="false"
                placeholder="Click to add AI analysis strategies to run" label="name" track-by="key" selectLabel=""
              />
            </div>
          </div>
          <div class="row">
            <div class="col-2">
              <label class="mr-1 font-weight-bold">Name</label>
            </div>
            <div class="col">
              <input type="text" v-model="newAnalysis.analysisName" class="form-control" placeholder="Enter group name" name="analysisName" autocomplete="exatom" data-1p-ignore/>
            </div>
          </div>
          <div class="row">
            <div class="col-2">
              <label class="mr-1 font-weight-bold">Strategies</label>
            </div>
            <div class="col">
              <multiselect
                v-model="newAnalysis.selectedStrategies" :options="availableStrategies.filter(s => [undefined, false].includes(s.hide)).sort((a, b) => a.name > b.name ? 1 : -1)" :multiple="true" :hide-selected="true" :close-on-select="false"
                placeholder="Click to add AI analysis strategies to run" label="name" track-by="key" selectLabel=""
              />
            </div>
          </div>
          <div class="row">
            <div class="col-2">
              <label class="mr-1 font-weight-bold">Forms</label>
            </div>
            <div class="col">
              <multiselect
                v-model="newAnalysis.selectedForms" :options="forms" :multiple="true" :hide-selected="true" :close-on-select="false"
                placeholder="Click to add a form to the AI analysis" label="display_name" track-by="id" selectLabel=""
              />
            </div>
          </div>
          <div class="row">
            <div class="col-2">
              <label class="mr-1 font-weight-bold">Date from</label>
            </div>
            <div class="col">
              <input type="date" v-model="newAnalysis.dates.from" class="form-control" />
            </div>
          </div>
          <div class="row">
            <div class="col-2">
              <label class="mr-1 font-weight-bold">Date to</label>
            </div>
            <div class="col">
              <input type="date" v-model="newAnalysis.dates.to" class="form-control" />
            </div>
          </div>
          <div class="d-flex justify-content-end">
            <button type="submit" class="btn btn-primary" :disabled="!isValidAnalysis || newAnalysis.submitInProgress">
              <span class="material-symbols-outlined" style="vertical-align: text-bottom;">automation</span> Start analysis <b-spinner variant="primary" v-if="newAnalysis.submitInProgress"></b-spinner>
            </button>
          </div>
        </form>
      </div>
    </collapse-transition>

    <div class="mt-3" style="display: flex; gap: 20px;" v-if="totalAnalyses">
      <div class="card" id="batchList" style="flex: 0 0 350px;">
        <div class="d-flex justify-content-between align-items-start mb-3">
          <h3 class="m-0 text-gradient-blue-green text-uppercase" style="font-weight: 900;">AI Analyses</h3>
          <button class="btn btn-xs btn-light" @click="loadAnalyses">
            <i class="material-icons" style="font-size: 18px; vertical-align: text-bottom;">refresh</i>
          </button>
        </div>
        <div v-for="(group, key) in analysesByDateGroups" :key="key">
          <div class="mb-3" v-if="group.length">
            <h5 class="text-uppercase bold">
              <span class="material-symbols-outlined" style="vertical-align: text-top;">calendar_month</span>
              {{ humanFriendlyDate(key) }}
            </h5>
            <div v-for="(analysis, timeGroup) in group" :key="timeGroup" class="card p-2 mb-2 session-item" style="cursor: pointer;" @click.prevent="selectAnalysis(analysis)" :class="{ 'selected': selectedAnalysis && selectedAnalysis?.analysisId === analysis?.analysisId }">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <span class="font-weight-bold text-primary">{{ analysis?.name }}</span>
                <!-- Presentation icon for COMPLETED status -->
                <span
                    v-if="analysis.presentationStatus === 'COMPLETED'"
                    v-b-tooltip
                    title="Slides available for this analysis"
                    class="material-symbols-outlined filled text-success mr-2"
                    style="font-size: 18px; vertical-align: text-bottom;">
                  slideshow
                </span>

                <!-- Loading icon for RUNNING status -->
                <span
                  v-if="analysis.presentationStatus === 'RUNNING'"
                  v-b-tooltip
                  title="Slides are being prepared"
                  class="material-symbols-outlined filled text-warning mr-2"
                  style="font-size: 18px; vertical-align: text-bottom;">
                  slideshow
                </span>

                <!-- Loading icon for ERROR status -->
                <span
                  v-if="analysis.presentationStatus === 'ERROR'"
                  v-b-tooltip
                  title="There was an error preparing your slides. Click to rebuild your slides."
                  class="material-symbols-outlined filled text-danger mr-2"
                  style="font-size: 18px; vertical-align: text-bottom; cursor: pointer;"
                  @click="rebuildPresentation(analysis)">
                  slideshow
                </span>
              </div>

              <div>
                <span class="badge badge-outline-secondary bg-light mr-1" style="border-radius: 4px;"
                      v-for="formName in new Set(analysis.strategies.map(s => s?.context?.form_name).filter(Boolean))" :key="formName">
                  {{ formName }}
                </span>
              </div>

              <div class="d-flex justify-content-between align-items-end mt-3">
                <div class="d-flex flex-column">
                  <small :title="analysis.createdAt | datetime">
                    <span class="material-symbols-outlined" style="font-size: 14px; vertical-align: text-bottom;">calendar_month</span>
                    {{ analysis.createdAt | dateRelative(dt.DATE_MED) }}
                  </small>
                  <small v-if="analysisInitiatorName(analysis)" :title="analysis.requestedBy?.email">
                    <span class="material-symbols-outlined" style="font-size: 14px; vertical-align: text-bottom;">person</span>
                    {{ analysisInitiatorName(analysis) }}
                  </small>
                </div>
                <badge-component v-model="analysis.status"/>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="w-100">
        <div class="card mb-4" style="flex: 1;" v-if="selectedAnalysis?.analysisId">
          <div class="d-flex justify-content-between align-items-start mb-3">
            <h3 class="text-primary">{{ selectedAnalysis.name }}</h3>
            <div class="d-flex align-items-center">
              <b-dropdown no-caret type="link" variant="link" class="mb-2" dropleft>
                <template #button-content>
                  <span class="monospace text-secondary">...</span>
                </template>
                <b-dropdown-item @click="rebuildPresentation(selectedAnalysis)"><i class="material-icons" style="font-size: 18px; vertical-align: text-bottom;">slideshow</i> Rebuild slides</b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
          <div>
            <form name="aiAnalysisSelectorForm">
              <div class="d-flex" style="flex-wrap: wrap; gap: 1rem;">
                <div class="card card-body mb-0"
                     style="max-width: 300px;"
                     v-for="(strategies, formHash) in analysisResultsByForm" :key="formHash">
                  <div class="d-flex display-7 text-ellipsis font-weight-bold align-items-center mb-2" :title="forms.find(f => f.id === formHash)?.display_name ?? 'All forms'">
                    <div class="icon-form small">&nbsp;</div> {{ forms.find(f => f.id === formHash)?.display_name ?? 'All forms' }}
                  </div>
                  <label v-for="strategy in strategies" :key="strategy.strategyId" class="ml-3 mb-0 mt-2">
                    <input name="aiAnalysisSelectorStrategy" type="radio" @change="selectStrategy(strategy)" :checked="strategy.strategyId === selectedStrategy?.strategyId" style="vertical-align: middle;" />
                    {{ getStrategyName(strategy.type) }} {{ strategy.type !== 'SUMMARY' ? 'report' : '' }}
                  </label>
                </div>

                <!-- Presentation -->
                <div class="card card-body mb-0"
                     style="max-width: 300px;"
                     v-if="presentationReady">
                  <div class="d-flex display-7 text-ellipsis font-weight-bold align-items-center mb-2" title="Slides for your analysis">
                    <span class="material-symbols-outlined text-primary mr-1" style="font-size: 18px; vertical-align: sub;">slideshow</span> Ready to present
                  </div>
                  <b-button variant="primary" @click="downloadPresentation(selectedAnalysis)"><i class="material-symbols-outlined" style="font-size: 18px; vertical-align: text-bottom;">download</i> Download Slides</b-button>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div class="card" v-if="selectedStrategyResults && selectedStrategy?.type">
          <analysis-details
            :title="`${getStrategyName(selectedStrategy.type)} ${selectedStrategy.type !== 'SUMMARY' ? 'report' : ''} for ${forms.find(f => f.id === selectedStrategy.formHash)?.display_name ?? ' all forms'}`"
            :strategy-details="selectedStrategyResults"
            :strategy-prompt="selectedStrategyPrompt"
            :suggestions="suggestions"
            :loading-suggestions="loadingSuggestions"
            :on-suggestion-click="handleSuggestionClick"
          />
        </div>

        <div class="d-flex justify-content-end" v-if="selectedAnalysis && selectedAnalysis.analysisId && canDelete">
          <button class="btn btn-danger small" @click="removeAnalysis(selectedAnalysis)">Remove analysis</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style src="@/vendor/libs/vue-multiselect/vue-multiselect.scss" lang="scss"></style>

<style scoped>
FORM .row {
  margin-bottom: 1rem;
}

FORM .col-2 {
  text-align: right;
  align-self: end;
}

.card {
  border: 1px solid #ccc;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}
.selected {
  background-color: var(--exatom-blue-100) !important;
}
.pre {
  background-color: #E9E9EA;
}
.collapse-transition-enter-active,
.collapse-transition-leave-active {
  transition: all 0.3s ease;
}
.collapse-transition-enter,
.collapse-transition-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}
.nav-tabs .nav-link {
  cursor: pointer;
}
:deep(.badge) {
  margin-bottom: 0 !important;
}
</style>

<script>
import { DateTime } from 'luxon';
import CollapseTransition from '@/components/CollapseTransition.vue';
import axios from 'axios';
import BadgeComponent from '@/components/BadgeComponent.vue';
import AnalysisDetails from '@/components/insights/eureka/AnalysisDetails.vue';
import Multiselect from 'vue-multiselect';
import { download } from "@/utils";

export default {
  name: 'AnalysisSessions',
  metaInfo: {
    title: 'AI Form Analysis'
  },
  components: { Multiselect, BadgeComponent, CollapseTransition, AnalysisDetails },
  data() {
    return {
      loading: true,

      dt: DateTime,
      selectedClient: null,

      showNewAnalysis: false,
      newAnalysis: {
        selectedScenario: undefined,
        analysisName: 'Analysis and Recommendations',
        selectedForms: [],
        selectedStrategies: [],
        dates: {
          from: DateTime.now().minus({ days: 30 }).toISODate(),
          to: DateTime.now().minus({ days: 1 }).toISODate()
        },
        submitInProgress: false
      },

      availableScenarios: [
        {
          key: 'a&r',
          name: 'Analysis and Recommendations',
          strategies: [
            'CONVERSION_LEAKS',
            'DEVICE_COMPARISON',
            'FIELDS_INSIGHTS',
            'FORM_ERRORS',
            'AUTOFILL_CORRELATION',
            'INDUSTRY_BENCHMARKS',
            'TOP_SOURCES'
          ],
          //dates: {from, to}
        },
        {
          key: 'qbr',
          name: 'Quarterly Business Review',
          strategies: [
            'CONVERSION_LEAKS',
            'DEVICE_COMPARISON',
            'FIELDS_INSIGHTS',
            'FORM_ERRORS',
            'AUTOFILL_CORRELATION',
            'INDUSTRY_BENCHMARKS',
            'TOP_SOURCES'
          ],
        },
        {
          key: 'monthly-meeting',
          name: 'Monthly Meeting',
          strategies: [
            'CONVERSION_LEAKS',
            'DEVICE_COMPARISON',
            'FIELDS_INSIGHTS',
            'FORM_ERRORS',
            'AUTOFILL_CORRELATION',
            'TOP_SOURCES'
          ],
        },
        {
          key: 'custom',
          name: 'Custom',
          strategies: []
        }
      ],
      availableStrategies: [
        { key: 'CONVERSION_LEAKS', name: 'Conversion Leaks' },
        { key: 'DEVICE_COMPARISON', name: 'Device Comparison' },
        { key: 'AUTOFILL_CORRELATION', name: 'Autofill Correlation' },
        { key: 'FIELDS_INSIGHTS', name: 'Fields Insights' },
        { key: 'FORM_ERRORS', name: 'Form Errors' },
        { key: 'INDUSTRY_BENCHMARKS', name: 'Industry Benchmarks' },
        { key: 'TOP_SOURCES', name: 'UTM Performance' },
        { key: 'SUMMARY', name: 'Management Summary', hide: true}
      ],

      forms: [],

      analysesByDateGroups: {},
      selectedAnalysis: {},
      selectedStrategy: null,
      selectedStrategyResults: {},
      selectedStrategyPrompt: '',

      suggestions: [],
      loadingSuggestions: false,

      pendingAnalysesInterval: null
    };
  },
  computed: {
    aiCreditsTotal () {
      const credits = this.$root.getWebsiteProperty('AI credits - monthly limit')?.value
      if (credits) {
        return Number.parseInt(credits)
      }
      return 0
    },
    aiCreditsUsed () {
      const usage = this.$root.getWebsiteProperty('AI credits - usage')?.value
      if (usage) {
        return Number.parseInt(usage)
      }
      return 0
    },
    aiCreditsUsedPct () {
      if (!this.aiCreditsUsed) return 0
      return (this.aiCreditsUsed / this.aiCreditsTotal * 100).toFixed(2)
    },
    canDelete () {
      return this.$root.hasRole('admin.internal') ||
        this.$root.hasRole('admin.company') ||
        this.$root.hasRole('configuration.delete')
    },
    analysisResultsByForm() {
      if (!this.selectedAnalysis || !Object.keys(this.selectedAnalysis).length) return []

      const formStrategyResults = {}
      for (const strategy of this.selectedAnalysis.strategies) {
        if (!formStrategyResults[strategy.formHash]) {
          formStrategyResults[strategy.formHash] = []
        }
        formStrategyResults[strategy.formHash].push(strategy)
      }

      return Object.fromEntries(
        Object.entries(formStrategyResults)
          .map(([key, value]) => [
            key,
            value.sort((a, b) => (a?.context?.form_name || "").localeCompare(b?.context?.form_name || ''))
          ])
      );
    },
    screenshots() {
      if (!this.selectedStrategyResults?.step_results?.length) return [];
      return this.selectedStrategyResults.step_results.filter(s => s.uri);
    },
    isValidAnalysis() {
      return (
        this.newAnalysis.analysisName.trim() !== '' &&
        this.newAnalysis.selectedStrategies.length > 0 &&
        this.newAnalysis.selectedForms.length > 0 &&
        this.newAnalysis.dates.from &&
        this.newAnalysis.dates.to
      );
    },
    totalAnalyses () {
      let totalAnalyses = 0
      for (const dateGroup of Object.values(this.analysesByDateGroups)) {
        for (const analysis of dateGroup) {
          totalAnalyses++
        }
      }
      return totalAnalyses
    },
    pendingAnalyses() {
      let pendingAnalyses = []
      for (const dateGroup of Object.values(this.analysesByDateGroups)) {
        for (const analysis of dateGroup) {
          if (!['ERROR', 'COMPLETED'].includes(analysis.status)) {
            pendingAnalyses.push(analysis)
          }
        }
      }
      return pendingAnalyses
    },
    allAnalyses() {
      let allAnalyses = []
      for (const dateGroup of Object.values(this.analysesByDateGroups)) {
        for (const analysis of dateGroup) {
          allAnalyses.push(analysis)
        }
      }
      return allAnalyses
    },
    presentationReady () {
      return this.selectedAnalysis?.presentationStatus === 'COMPLETED'
    },

  },
  methods: {
    toggleNewAnalysis() {
      if (this.aiCreditsTotal && this.aiCreditsUsedPct >= 100) {
        this.$bvModal.msgBoxOk('You’ve reached your AI usage limit for this billing period, so AI features are temporarily unavailable. Your limit will automatically reset by the end of the month. Our AI chat will continue to be available to you.', {
          title: 'AI credit limit exceeded',
          okVariant: 'primary',
        })
        return
      }

      this.showNewAnalysis = !this.showNewAnalysis;

      if (typeof this.newAnalysis?.selectedScenario === 'undefined') {
        this.newAnalysis.selectedScenario = this.availableScenarios[0];
      }
    },
    getStrategyName(strategyKey) {
      const strategy = this.availableStrategies.find(s => s.key === strategyKey);
      if (!strategy) return 'Unknown strategy';
      return strategy.name;
    },
    formatTime(dateStr) {
      return DateTime.fromISO(dateStr).toFormat('HH:mm');
    },
    humanFriendlyDate(key) {
      const mapping = {
        today: 'Today',
        yesterday: 'Yesterday',
        last_7_days: 'Last 7 Days',
        last_30_days: 'Last 30 Days',
        older: 'Older'
      };
      return mapping[key] || key;
    },
    analysisInitiatorName(analysis) {
      if (!analysis?.requestedBy) return '';
      if (analysis.requestedBy.name) return analysis.requestedBy.name;
      if (analysis.requestedBy.id === this.$root.me?.id) return this.$root.me.name;
      return analysis.requestedBy.email || '';
    },
    async loadAnalyses() {
      if (!this.selectedClient) return;

      const response = await axios.get(`${process.env.VUE_APP_EUREKA_API}/api/analysis/${this.selectedClient?.client_hash}${this.pendingAnalysesInterval?'#axios-hide':''}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('apiToken')}`,
          'X-Client-Hash': this.$root.client.client_hash
        }
      }).catch(e => {
        console.error(e)
        return
      });
      this.analysesByDateGroups = response.data;

      if (this.selectedAnalysis?.status) {
        if (!['ERROR', 'COMPLETED'].includes(this.selectedAnalysis.status) ) {
          // status PENDING, RUNNING, ... -> so reload
          this.selectedAnalysis = await this.getAnalysis(this.selectedAnalysis);
          await this.selectAnalysis(this.selectedAnalysis, this.selectedStrategy);
        }
        if (!['ERROR', 'COMPLETED'].includes(this.selectedAnalysis.presentationStatus)) {
          // presentationStatus RUNNING, ... -> also reload
          this.selectedAnalysis = await this.getAnalysis(this.selectedAnalysis);
          await this.selectAnalysis(this.selectedAnalysis, this.selectedStrategy);
        }
      } else if (this.totalAnalyses) {
        // Pre-select an item if there are any and nothing is currently selected
        await this.selectAnalysis(this.allAnalyses[0])
      }

      if (!this.pendingAnalyses.length && (!this.selectedAnalysis || ['ERROR', 'COMPLETED'].includes(this.selectedAnalysis.presentationStatus))) {
        clearInterval(this.pendingAnalysesInterval)
      }

      // Check route only if we don't have a valid selection that matches the route
      const currentRouteId = this.$route?.params?.analysisId || this.$route?.query?.analysisId;
      if (currentRouteId && this.selectedAnalysis?.analysisId !== currentRouteId) {
         this.checkRouteAnalysisId();
      }
    },
    async getAnalysis(analysis) {
      if (!this.selectedClient || !analysis?.analysisId) return;
      const response = await axios.get(`${process.env.VUE_APP_EUREKA_API}/api/analysis/${this.selectedClient?.client_hash}/${analysis?.analysisId}${this.pendingAnalysesInterval?'#axios-hide':''}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('apiToken')}`,
          'X-Client-Hash': this.$root.client.client_hash
        }
      });
      return response.data;
    },
    async removeAnalysis(analysis) {
      if (!this.selectedClient || !analysis?.analysisId || !this.canDelete) return;

      if (!await this.$bvModal.msgBoxConfirm('Are you sure you want to remove "' + this.selectedAnalysis.name + '"?', {
        title: 'Remove analysis confirmation',
        okVariant: 'danger',
        cancelVariant: 'iso-link'
      })) return;

      await axios.delete(`${process.env.VUE_APP_EUREKA_API}/api/analysis/${this.selectedClient?.client_hash}/${analysis?.analysisId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('apiToken')}`,
          'X-Client-Hash': this.$root.client.client_hash
        }
      });
      this.selectedAnalysis = {};
      this.selectedStrategy = null;
      this.selectedStrategyResults = {};
      await this.loadAnalyses();
    },
    async selectAnalysis(analysis, strategy = null, forceStrategyReload = true) {
      if (!analysis) return

      // If we are selecting the exact same analysis, just update the reference
      // to keep data fresh, but don't necessarily re-trigger strategy loading unless needed.
      const isSameAnalysis = this.selectedAnalysis?.analysisId === analysis.analysisId;
      this.selectedAnalysis = analysis;

      // Update the analysis in the list to ensure the list view is up to date
      if (analysis.status !== 'COMPLETED') {
        this.selectedAnalysis = await this.getAnalysis(analysis);
        for (const dateGroup of Object.values(this.analysesByDateGroups)) {
          if (dateGroup.length) {
            const foundIndex = dateGroup.findIndex(item => item.analysisId === this.selectedAnalysis.analysisId);
            if (foundIndex !== -1) {
              // Use $set or direct assignment if reactive (Vue 2 caveat: direct assignment by index might not be reactive without $set)
              this.$set(dateGroup, foundIndex, this.selectedAnalysis);
            }
          }
        }
      }

      // Determine strategy to select
      let targetStrategy = strategy;
      if (!targetStrategy) {
         if (this.selectedAnalysis?.strategies?.length) {
            targetStrategy = this.selectedAnalysis.strategies[0];
         }
      }

      // If we provided a specific strategy, or fell back to the first one
      if (targetStrategy) {
         // Determine if we need to reload the strategy details.
         // 1. If forceStrategyReload is true (default) -> reload.
         // 2. If the selected strategy changed -> reload.
         // 3. If it's the same analysis and same strategy, but we just refreshed analysis status
         const isSameStrategy = this.selectedStrategy?.strategyId === targetStrategy.strategyId;
         if (forceStrategyReload || !isSameAnalysis || !isSameStrategy) {
            await this.selectStrategy(targetStrategy);
         }
      }
    },
    async selectStrategy(strategy) {
      const isSameStrategy = this.selectedStrategy?.strategyId === strategy.strategyId;
      this.selectedStrategy = strategy;

      // Only clear suggestions if we are switching to a DIFFERENT strategy.
      // If we are just refreshing the current one, keeping old suggestions prevents flickering.
      if (!isSameStrategy) {
        this.$set(this, 'suggestions', []);
      }

      this.loadingSuggestions = true;
      await this.loadStrategyDetails(this.selectedAnalysis.clientHash, this.selectedAnalysis.analysisId, strategy.strategyId);
    },
    async loadStrategyDetails(clientHash, analysisId, strategyId) {
      try {
        const response = await axios.get(`${process.env.VUE_APP_EUREKA_API}/api/analysis/${clientHash}/${analysisId}/${strategyId}${this.pendingAnalysesInterval?'#axios-hide':''}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('apiToken')}`,
            'X-Client-Hash': this.$root.client.client_hash
          }
        });
        this.selectedStrategyResults = response.data;
        if (this.strategyReadyForSuggestions(this.selectedStrategyResults)) {
          await this.loadSuggestions(clientHash, analysisId, strategyId);
        } else {
          this.$set(this, 'suggestions', []);
          this.loadingSuggestions = false;
        }

        //Load the prompt if the user has the right permissions
        if (this.$root.hasRole('admin.internal')) {
          try {
            const response = await axios.get(`${process.env.VUE_APP_EUREKA_API}/api/analysis/${clientHash}/${analysisId}/${strategyId}/prompt${this.pendingAnalysesInterval?'#axios-hide':''}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('apiToken')}`,
                'X-Client-Hash': this.$root.client.client_hash
              }
            });
            this.selectedStrategyPrompt = response.data;
          } catch (error) {
            console.error('Failed to load prompt:', error);
          }
        }
      } catch (error) {
        console.error('Error loading strategy details:', error);
        this.loadingSuggestions = false;
      }
    },
    async loadSuggestions(clientHash, analysisId, strategyId) {
      if (!clientHash || !analysisId || !strategyId) return;

      if (!this.strategyReadyForSuggestions(this.selectedStrategyResults)) {
        this.loadingSuggestions = false;
        return;
      }

      this.loadingSuggestions = true;

      try {
        const response = await axios.get(`${process.env.VUE_APP_EUREKA_API}/api/analysis/${clientHash}/${analysisId}/${strategyId}/suggestions${this.pendingAnalysesInterval?'#axios-hide':''}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('apiToken')}`,
            'X-Client-Hash': this.$root.client.client_hash
          }
        });
        let payload = response.data;
        if (typeof payload === 'string') {
          try {
            payload = JSON.parse(payload);
          } catch (error) {
            console.warn('[Eureka][Suggestions] Failed to parse suggestions JSON string from API', error);
          }
        }

        const storedSuggestions = this.normalizeSuggestionsPayload(payload);
        this.$set(this, 'suggestions', Array.isArray(storedSuggestions) ? storedSuggestions : []);
      } catch (error) {
        if (error?.response?.status !== 404) {
          console.error('Error loading stored suggestions:', error);
        }
        this.$set(this, 'suggestions', []);
      } finally {
        this.loadingSuggestions = false;
      }
    },
    normalizeSuggestionsPayload(payload) {
      if (!payload && payload !== '') return [];

      if (Array.isArray(payload)) {
        return payload;
      }

      if (typeof payload === 'string') {
        try {
          const parsed = JSON.parse(payload);
          return this.normalizeSuggestionsPayload(parsed);
        } catch (error) {
          console.warn('[Eureka][Suggestions] Unable to parse string payload for suggestions', error);
          return [];
        }
      }

      if (typeof payload !== 'object') {
        return [];
      }

    const candidateKeys = ['suggestions', 'request_payload', 'data', 'payload'];
      for (const key of candidateKeys) {
        if (payload?.[key]) {
          if (payload[key] === payload) continue;
          const normalized = this.normalizeSuggestionsPayload(payload[key]);
          if (normalized.length) {
            return normalized;
          }
        }
      }

      return [];
    },
    strategyReadyForSuggestions(result) {
      const status = result?.request_payload?.status;
      const hasText = !!result?.final_result?.text;
      return status === 'COMPLETED' && hasText;
    },
    industryVerticalString() {
      const iv = this.selectedClient?.industry_verticals;
      return iv?.length ? iv.map(i => i.name).join(', ') : 'not set';
    },
    async downloadPresentation(analysis) {
      if (analysis.presentationStatus !== 'COMPLETED' || !analysis.clientHash) return;

      try {
        await download(await this.$api.get(`/eureka/${analysis.analysisId}/download`,this.$root.client.client_hash, { responseType: 'blob' }))
      } catch (error) {
        console.error('Error downloading slides:', error);
      }
    },
    async rebuildPresentation(analysis) {
      if (!analysis.clientHash || !analysis.latestPresentationId) return;

      if (
        ['RUNNING'].includes(analysis.presentationStatus) &&
        !await this.$bvModal.msgBoxConfirm('There is already a rebuild running, do you want to run it again?', {
          title: 'Rebuild confirmation',
          okVariant: 'warning',
          cancelVariant: 'iso-link'
        })
      ) return;

      try {
        await axios.get(
            `${process.env.VUE_APP_EUREKA_API}/api/presentation/rebuild/${analysis.clientHash}/${analysis.analysisId}/${analysis.latestPresentationId}`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('apiToken')}`,
                'X-Client-Hash': this.$root.client.client_hash
              }
            }
        );

        this.selectedAnalysis.presentationStatus = 'RUNNING'; // To avoid lag disabling the button

        // Give the job a few seconds to start
        setTimeout(async () => {
          await this.loadAnalyses()
          if (this.selectedAnalysis.presentationStatus === 'RUNNING') {
            this.pendingAnalysesInterval = setInterval(() => {
              this.loadAnalyses();
            }, 15000)
          }
        }, 10000)
      } catch (error) {
        console.error('Error rebuilding slides:', error);
      }
    },
    formCategories(form) {
      const fc = form?.categories;
      return fc?.length ? fc.map(c => c.name).join(', ') : 'not set';
    },
    async submitNewAnalysis() {
      if (!this.isValidAnalysis) return;

      this.newAnalysis.submitInProgress = true;
      const payload = {
        clientHash: this.selectedClient?.client_hash,
        clientName: this.selectedClient?.name,
        name: this.newAnalysis.analysisName,
        strategies: []
      };

      this.newAnalysis.selectedStrategies.forEach(strategy => {
        this.newAnalysis.selectedForms.forEach(form => {
          payload.strategies.push({
            formHash: form?.id,
            from: this.newAnalysis.dates.from,
            to: this.newAnalysis.dates.to,
            type: strategy.key,
            context: {
              client_industry: this.industryVerticalString(),
              form_name: form?.display_name,
              form_category: this.formCategories(form)
            }
          })
        })
      });

      try {
        const analysis = await axios.post(`${process.env.VUE_APP_EUREKA_API}/api/analysis/`, payload, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('apiToken')}`,
            'X-Client-Hash': this.$root.client.client_hash
          }
        });
        this.showNewAnalysis = false;
        this.newAnalysis.submitInProgress = false;

        await this.loadAnalyses();
        if (this.pendingAnalyses.length) {
          setTimeout(() => this.loadAnalyses(), 5000)
          this.pendingAnalysesInterval = setInterval(() => this.loadAnalyses(), 30000)

          const h = this.$createElement;
          const toastBody = h('div', { class: '' }, [
            h('div', { class: 'mb-3' }, [
              'We\'ll send a notification and follow up with an email when it\'s ready.'
            ]),
            h('div', { class: '' }, [
              'Feel free to keep working in the interface while the analysis runs in the background.'
            ]),
          ])
          this.$root.$bvToast.toast(toastBody, {
            title: 'AI Analysis started',
            solid: true,
            variant: 'success',
          });

          setTimeout(() => {
            this.$root.checkAIAnalysisCompletion(analysis.data);
          }, 5000);
        }
      } catch (error) {
        this.newAnalysis.submitInProgress = false;
        console.error('Error submitting analysis:', error);
      }
    },
    setAnalysisName (name = null) {
      if (name) {
        this.newAnalysis.analysisName = name;
      } else if (this.$root.client && this.newAnalysis.selectedScenario?.name) {
        this.newAnalysis.analysisName = this.newAnalysis.selectedScenario.name;
      }
    },
    async handleSuggestionClick(suggestion) {
      const dateRange = {
        from: this.selectedStrategyResults?.request_payload?.from || null,
        to: this.selectedStrategyResults?.request_payload?.to || null
      };

      this.$root.updateAiChatMetadata({
        formHash: this.selectedStrategy?.formHash || null,
        dateRange,
        maxSuggestions: 6
      });

      if (this.$root && typeof this.$root.$emit === 'function') {
        this.$root.$emit('ai-chat:start-new-session');
      }

      // Open the AI chat sidebar
      await this.$root.showAiChatSidebar();

      // Wait for sidebar to be visible and chat widget to be ready
      this.$nextTick(() => {
        setTimeout(() => {
          // Find the AiChatSidebar component and access its chat widget
          // The AiChatSidebar is rendered in Layout2.vue with ref="chatSidebar"
          // However, we need to access it through the chat widget's methods
          const chatContainer = document.querySelector('.ai-chat-sidebar .n8n-chat-container');
          if (chatContainer) {
            // Find input field
            const inputSelectors = [
              'input[type="text"]',
              'textarea',
              '[contenteditable="true"]',
              '.chat-input input',
              '.chat-input textarea'
            ];

            let inputElement = null;
            for (const selector of inputSelectors) {
              inputElement = chatContainer.querySelector(selector);
              if (inputElement) break;
            }

            if (inputElement) {
              // Insert the prompt
              if (inputElement.tagName === 'INPUT' || inputElement.tagName === 'TEXTAREA') {
                inputElement.value = suggestion.prompt;
                inputElement.focus();
                inputElement.dispatchEvent(new Event('input', { bubbles: true }));
                inputElement.dispatchEvent(new Event('change', { bubbles: true }));
              } else if (inputElement.contentEditable === 'true') {
                inputElement.textContent = suggestion.prompt;
                inputElement.focus();
                inputElement.dispatchEvent(new Event('input', { bubbles: true }));
              }

              // Submit the chat after a short delay
              setTimeout(() => {
                const sendBtnSelectors = [
                  'button[type="submit"]',
                  '.chat-input button',
                  '.chat-input-send-button',
                  '[data-testid="chat-send-button"]'
                ];

                let sendBtn = null;
                for (const selector of sendBtnSelectors) {
                  sendBtn = chatContainer.querySelector(selector);
                  if (sendBtn) break;
                }

                if (sendBtn) {
                  sendBtn.click();
                } else {
                  // Fallback: simulate Enter key
                  inputElement.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true }));
                  inputElement.dispatchEvent(new KeyboardEvent('keypress', { key: 'Enter', code: 'Enter', bubbles: true }));
                  inputElement.dispatchEvent(new KeyboardEvent('keyup', { key: 'Enter', code: 'Enter', bubbles: true }));
                }
              }, 100);
            }
          }
        }, 300); // Wait for sidebar animation to complete
      });
    },
    async checkRouteAnalysisId() {
      const analysisId = this.$route?.params?.analysisId || this.$route?.query?.analysisId;
      if (!analysisId || !this.analysesByDateGroups) return;
      for (const dateGroup of Object.values(this.analysesByDateGroups)) {
        const found = dateGroup.find(a => a.analysisId === analysisId);
        if (found) {
          await this.selectAnalysis(found);

          // Remove items from the url, otherwise when we click another and then hit the "refresh UI button" it will be selected again
          // -> For example when clicked on an 'it is ready notification' or email
          const uri = this.$router.resolve({name: 'eureka'}).href
          history.replaceState({}, '', uri)

          break;
        }
      }
    },
    subscriptionUsageColor (pct) {
      if (pct < 90) {
        return 'primary'
      } else if (pct >= 90 && pct < 100) {
        return 'warning'
      } else {
        return 'danger'
      }
    },
  },
  async mounted() {
    await this.$msgBoxAIConsent();
    this.checkRouteAnalysisId();

    // Enable chat suggestions metadata which is required for the sidebar to be visible in Layout2
    this.$root.setAiChatMetadata({
      suggestionsEnabled: true,
      maxSuggestions: 6,
    });

    // Disable automatic AI chat sidebar visibility for this page
    // The chat should only open when clicking a suggestion button
    this.$root.aiChatSidebarVisible = false;
  },
  beforeDestroy() {
    clearInterval(this.pendingAnalysesInterval);
  },
  watch: {
    'newAnalysis.selectedScenario': {
      async handler() {
        if (this.$root.client) {
          this.setAnalysisName()

          // Set the linked strategies
          this.newAnalysis.selectedStrategies = [];
          for (const strategy of (this.newAnalysis.selectedScenario?.strategies ?? [])) {
            this.newAnalysis.selectedStrategies.push(this.availableStrategies.find(s => s.key === strategy));
          }
        }
      }
    },
    '$root.client': {
      async handler() {
        this.loading = true

        if (this.$root.client) {
          this.selectedClient = this.$root.client;

          // Reset
          this.newAnalysis = {
            ...this.newAnalysis,
            // Keep dates & selected strategies & scenario
            analysisName: '',
            selectedForms: [],
            submitInProgress: false
          }

          this.setAnalysisName()

          this.forms = (await this.$api.get('/forms?with=categories', this.$root.client.client_hash)).data.sort((a, b) =>
              a.display_name.replace(/[^a-z0-9_-]+/gi, '') > b.display_name.replace(/[^a-z0-9_-]+/gi, '') ? 1 : -1
          );

          await this.loadAnalyses();
        }

        this.loading = false
      },
      immediate: true
    },
    '$route.query.analysisId': {
      handler() {
        this.checkRouteAnalysisId();
      },
      immediate: true
    },
  }
};
</script>
